import { FruitSizeMeasurement } from '../types/fruitsize';

const CACHE_KEY = 'fruitSizeMeasurements';
const SYNC_STATUS_KEY = 'measurementSyncStatus';

interface SyncStatus {
  lastSyncTime: number;
  pendingCount: number;
  lastError?: string;
}

export class MeasurementCacheService {
  
  // Save measurements to localStorage
  static saveMeasurements(measurements: FruitSizeMeasurement[]): void {
    try {
      const cacheMeasurements = measurements.filter(m => m.status !== 'saved');
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheMeasurements));
    } catch (error) {
      console.error('Error saving measurements to cache:', error);
    }
  }

  // Load measurements from localStorage
  static loadMeasurements(): FruitSizeMeasurement[] {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const measurements = JSON.parse(cached);
        const updatedMeasurements = measurements.map((m : FruitSizeMeasurement) => ({ ...m, selected: false }));
        return updatedMeasurements;
      }
    } catch (error) {
      console.error('Error loading measurements from cache:', error);
    }
    return [];
  }

  // Add a new measurement to cache
  static addMeasurement(measurement: FruitSizeMeasurement): void {
    const measurements = this.loadMeasurements();
    measurements.push(measurement);
    this.saveMeasurements(measurements);
  }

  // Update an existing measurement in cache
  static updateMeasurement(updatedMeasurement: FruitSizeMeasurement): void {
    const measurements = this.loadMeasurements();
    const index = measurements.findIndex(m => m.id === updatedMeasurement.id);
    if (index !== -1) {
      measurements[index] = updatedMeasurement;
      this.saveMeasurements(measurements);
    }
  }

  // Delete measurements from cache
  static deleteMeasurements(measurementIds: string[]): void {
    const measurements = this.loadMeasurements();
    const filteredMeasurements = measurements.filter(m => !measurementIds.includes(m.id));
    this.saveMeasurements(filteredMeasurements);
  }

  // Get measurements for a specific tree
  static getMeasurementsForTree(treeId: string): FruitSizeMeasurement[] {
    const measurements = this.loadMeasurements();
    return measurements.filter(m => m.treeID === treeId);
  }

  // Get pending measurements (not yet synced to backend)
  static getPendingMeasurements(): FruitSizeMeasurement[] {
    const measurements = this.loadMeasurements();
    return measurements.filter(m => m.status === 'new' || m.status === 'deleted');
  }

  // Mark measurements as synced
  static markAsSynced(measurementIds: string[]): void {
    const measurements = this.loadMeasurements();
    const updatedMeasurements = measurements.map(m => 
      measurementIds.includes(m.id) 
        ? { ...m, status: 'saved' as const }
        : m
    );
    this.saveMeasurements(updatedMeasurements);
  }

  // Save sync status
  static saveSyncStatus(status: SyncStatus): void {
    try {
      localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(status));
    } catch (error) {
      console.error('Error saving sync status:', error);
    }
  }

  // Load sync status
  static loadSyncStatus(): SyncStatus {
    try {
      const cached = localStorage.getItem(SYNC_STATUS_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
    return { lastSyncTime: 0, pendingCount: 0 };
  }

  // Clear all cached data
  static clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(SYNC_STATUS_KEY);
      console.log('Cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Get cache statistics
  static getCacheStats(): { total: number; pending: number; synced: number } {
    const measurements = this.loadMeasurements();
    const pending = measurements.filter(m => m.status === 'new' || m.status === 'deleted').length;
    const synced = measurements.filter(m => m.status === 'saved').length;
    
    return {
      total: measurements.length,
      pending,
      synced
    };
  }

  // Check if there are unsynced measurements
  static hasPendingMeasurements(): boolean {
    const measurements = this.loadMeasurements();
    return measurements.some(m => m.status === 'new' || m.status === 'deleted');
  }
} 